import { CONFIG } from '../config';
import { getAuthClient } from '../auth/authClient';
import { AUTH_ENABLED } from '../auth/flags';

const AUTH_USER_KEY = 'auth.user';
const DEFAULT_TIMEOUT_MS = 30000; // 30 seconds

// Keys to clear on logout (user-specific data, not UI preferences)
const USER_DATA_KEYS = [
  'auth.user',
  'viewedSections',
  'bookmarkOverrides',
  // Queued "Add to my courses" share intent — must not survive logout, or a
  // different account on the same device could redeem it (see pendingShare.js).
  'graspd:pendingShareAccept',
];

// Optional helper for consumers that store an app-level auth user in localStorage.
// If nothing has been stored, this returns null and callers can fall back to CONFIG.USER_ID.
export function getAuthUser() {
  try {
    return JSON.parse(window.localStorage.getItem(AUTH_USER_KEY) || 'null');
  } catch {
    return null;
  }
}

// Clear user-specific data from localStorage on logout
export function clearUserData() {
  try {
    // Remove known user-specific keys
    USER_DATA_KEYS.forEach(key => {
      window.localStorage.removeItem(key);
    });
    // Remove any hiddenCourses_* keys (keyed by user ID)
    Object.keys(window.localStorage).forEach(key => {
      if (key.startsWith('hiddenCourses_')) {
        window.localStorage.removeItem(key);
      }
    });
  } catch (e) {
    // Ignore storage errors
  }
}

// Handle 401 by signing out - prevents stale session state
async function handleUnauthorized() {
  try {
    clearUserData();
    const auth = getAuthClient();
    await auth.signOut();
    // Redirect to login page if not already there
    if (!window.location.pathname.includes('/login')) {
      window.location.href = '/login';
    }
  } catch (e) {
    // Silently fail - the main error will still be thrown
  }
}

/**
 * apiFetch('/api/v1/...', { method, headers, body, timeout, signal })
 * - Falls back to same-origin if CONFIG.API_BASE_URL is missing
 * - Auto-JSON stringifies plain object bodies
 * - Throws readable errors on non-2xx
 * - Supports timeout (default 30s) and AbortController signal
 * - Auto-signs out on 401 Unauthorized
 */
export async function apiFetch(path, options = {}) {
  if (!path.startsWith('/')) {
    throw new Error(`apiFetch path must start with '/': received "${path}"`);
  }

  const base = (CONFIG && CONFIG.API_BASE_URL) ? CONFIG.API_BASE_URL : window.location.origin;

  // Intentionally no try/catch: a malformed base URL is a configuration bug
  // we want surfaced loudly. Silently retargeting to window.location.origin
  // previously masked a misconfigured REACT_APP_API_BASE_URL secret and sent
  // every API call to the apex domain instead of api.graspd.ai.
  const url = new URL(path, base);

  // Set up timeout with AbortController
  const timeoutMs = options.timeout ?? DEFAULT_TIMEOUT_MS;
  const controller = new AbortController();
  const timeoutId = timeoutMs > 0 ? setTimeout(() => controller.abort(), timeoutMs) : null;

  // Allow caller to pass their own signal - abort if either signals
  if (options.signal) {
    options.signal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  let finalOptions = {
    credentials: 'include',
    ...options,
    signal: controller.signal,
  };

  // If auth flag is passed, attach Firebase ID token
  const shouldAttachAuth = AUTH_ENABLED && options.auth !== false;
  if (shouldAttachAuth) {
    try {
      const auth = getAuthClient();
      const idToken = await auth.getIdToken();
      if (idToken) {
        finalOptions.headers = {
          ...(finalOptions.headers || {}),
          Authorization: `Bearer ${idToken}`,
        };
      }
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Auth token fetch failed:', e);
      }
      throw e;
    }
  }

  // Auto-stringify plain objects (not FormData/Blob/etc.)
  const b = finalOptions.body;
  if (b && typeof b === 'object' && !(b instanceof FormData) && !(b instanceof Blob)) {
    finalOptions = {
      ...finalOptions,
      headers: { 'Content-Type': 'application/json', ...(finalOptions.headers || {}) },
      body: JSON.stringify(b),
    };
  }

  let res;
  try {
    res = await fetch(url.toString(), finalOptions);
  } catch (e) {
    if (timeoutId) clearTimeout(timeoutId);
    if (e.name === 'AbortError') {
      // Tag these so callers can tell "we gave up waiting" (the server may well
      // have finished) apart from "the request never landed". Retry logic needs
      // that distinction — see resumePendingShare.isRetryable.
      const cancelled = Boolean(options.signal?.aborted);
      const err = new Error(cancelled ? 'Request cancelled' : 'Request timeout');
      err.code = cancelled ? 'CANCELLED' : 'TIMEOUT';
      throw err;
    }
    throw e;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }

  // If rawResponse is requested, return the Response object directly
  // Caller is responsible for handling status codes and parsing
  if (options.rawResponse) {
    return res;
  }

  // Handle 401 Unauthorized - sign out and redirect
  if (res.status === 401) {
    await handleUnauthorized();
    throw new Error('Session expired. Please sign in again.');
  }

  if (!res.ok) {
    // Try JSON first; if the server returned a structured error, keep the
    // code/message/data on the thrown Error so callers can branch on them.
    // Only fall back to the raw text body when JSON parsing fails — otherwise
    // the raw JSON blob leaks into user-visible error surfaces.
    let message = `HTTP ${res.status}`;
    let code;
    let data;
    let parsedJson = false;
    try {
      data = await res.clone().json();
      parsedJson = true;
      // Structured `ErrorResponse` shape: { status, error, message, path, timestamp }
      code = data?.error || data?.code;
      message =
        data?.message ||
        (Array.isArray(data?.errors) && data.errors.join(', ')) ||
        code ||
        message;
    } catch {
      try {
        const text = await res.text();
        if (text) message = text;
      } catch {}
    }
    const err = new Error(message);
    err.status = res.status;
    if (code) err.code = code;
    if (parsedJson && data) err.data = data;
    throw err;
  }

  try { return await res.json(); } catch { return { ok: true }; }
}

// Marks a non-quiz section as viewed so progress can update on the dashboard
export async function markSectionViewed({ courseId, lessonId, sectionId }) {
  if (!courseId || !lessonId || !sectionId) {
    throw new Error('markSectionViewed requires courseId, lessonId, and sectionId');
  }
  return apiFetch('/api/v1/section/mark-viewed', {
    method: 'POST',
    // apiFetch auto-JSON stringifies plain objects and adds Content-Type
    body: {
      courseId,
      lessonId,
      sectionId,
      // Where the learner was at this moment, so analytics (peak hour/day) are
      // in their local time and travel is handled correctly. getTimezoneOffset()
      // returns minutes BEHIND UTC, so negate it to get minutes EAST of UTC
      // (e.g. New York EDT -> -240, London BST -> +60). Optional server-side.
      tzOffsetMinutes: -new Date().getTimezoneOffset(),
    },
    // Auth is attached by default unless options.auth === false
  });
}
